import { useState, useCallback, useRef } from 'react';
import qs from 'qs';
import type { FacetFilter } from '@jbhi-fi/apis/src/googleSearch/types';

const DEBOUNCE_TIME = 400;

export type GoogleSearchState = {
  query: string;
  hitsPerPage: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  facets: Array<FacetFilter>;
  isSaleItems: boolean;
  isClearanceItems: boolean;
  isExcludeMarketplace: boolean;
};

const STANDARD_KEYS = [
  'query',
  'hitsPerPage',
  'sortBy',
  'order',
  'Price',
  'saleItems',
  'isClearanceItems',
  'excludeMarketplace',
];

export const parseUrlToGoogleState = (): GoogleSearchState => {
  const params = qs.parse(window.location.search.slice(1));
  const facets: Array<FacetFilter> = [];

  // Refinement list filters (all non-standard keys → checkbox facets)
  Object.keys(params).forEach(key => {
    if (STANDARD_KEYS.includes(key)) return;
    const raw = params[key];
    const values: Array<string> = Array.isArray(raw) ? (raw as Array<string>) : raw ? [raw as string] : [];
    values.forEach(v => {
      if (v) facets.push({ name: key, value: v });
    });
  });

  // Price range
  if (params.Price) {
    const priceObj = params.Price as { min?: string; max?: string };
    const min = priceObj.min ? parseFloat(priceObj.min) : 0;
    const max = priceObj.max ? parseFloat(priceObj.max) : Infinity;
    if (min > 0 || max < Infinity) {
      facets.push({ name: 'Price', value: [min, max] });
    }
  }

  // Sort — stored in URL as e.g. "price_asc"
  let sortBy: string | undefined;
  let order: 'asc' | 'desc' | undefined;
  if (params.sortBy && typeof params.sortBy === 'string') {
    const parts = params.sortBy.split('_');
    const lastPart = parts[parts.length - 1];
    if (lastPart === 'asc' || lastPart === 'desc') {
      order = lastPart;
      sortBy = parts.slice(0, -1).join('_');
    }
  }

  return {
    query: typeof params.query === 'string' ? params.query : '',
    hitsPerPage: params.hitsPerPage ? parseInt(params.hitsPerPage as string, 10) : 36,
    sortBy,
    order,
    facets,
    isSaleItems: params.saleItems === 'true',
    isClearanceItems: params.isClearanceItems === 'true',
    isExcludeMarketplace: params.excludeMarketplace === 'true',
  };
};

export const serializeGoogleStateToUrl = (state: GoogleSearchState): string => {
  const params: Record<string, any> = {};

  if (state.query) params.query = state.query;
  if (state.hitsPerPage !== 36) params.hitsPerPage = state.hitsPerPage;
  if (state.sortBy) params.sortBy = `${state.sortBy}_${state.order ?? 'asc'}`;
  if (state.isSaleItems) params.saleItems = 'true';
  if (state.isClearanceItems) params.isClearanceItems = 'true';
  if (state.isExcludeMarketplace) params.excludeMarketplace = 'true';
  // Checkbox facets: group by name (multiple values = array)
  const grouped: Record<string, Array<string>> = {};
  state.facets.forEach(f => {
    if (typeof f.value === 'string') {
      if (!grouped[f.name]) grouped[f.name] = [];
      grouped[f.name].push(f.value);
    } else if (Array.isArray(f.value)) {
      const [min, max] = f.value;
      const price: Record<string, string> = {};
      if (min > 0) price.min = String(min);
      if (max < Infinity) price.max = String(max);
      if (Object.keys(price).length) params.Price = price;
    }
  });
  Object.assign(params, grouped);

  return Object.keys(params).length ? `?${qs.stringify(params, { indices: false })}` : '';
};

export type UseGoogleSearchStateReturn = {
  state: GoogleSearchState;
  setHitsPerPage: (hitsPerPage: number) => void;
  setFacet: (name: string, value: string, checked: boolean) => void;
  setPriceRange: (min: number, max: number) => void;
  clearPriceRange: () => void;
  setSort: (sortBy: string | undefined, order: 'asc' | 'desc' | undefined) => void;
  setSaleItems: (isSaleItems: boolean) => void;
  setClearanceItems: (isClearanceItems: boolean) => void;
  setExcludeMarketplace: (isExcludeMarketplace: boolean) => void;
  clearAllFacets: () => void;
};

export const useGoogleSearchState = (): UseGoogleSearchStateReturn => {
  const [state, setState] = useState<GoogleSearchState>(() => parseUrlToGoogleState());
  const debounceRef = useRef<number | undefined>(undefined);

  const pushUrl = useCallback((newState: GoogleSearchState) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const url = `${window.location.pathname}${serializeGoogleStateToUrl(newState)}`;
      window.history.pushState(newState, '', url);
    }, DEBOUNCE_TIME);
  }, []);

  const setFacet = useCallback(
    (name: string, value: string, checked: boolean) => {
      setState(prev => {
        const next: GoogleSearchState = {
          ...prev,
          facets: checked
            ? [...prev.facets.filter(f => !(f.name === name && f.value === value)), { name, value }]
            : prev.facets.filter(f => !(f.name === name && f.value === value)),
        };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const setHitsPerPage = useCallback(
    (hitsPerPage: number) => {
      setState(prev => {
        const next: GoogleSearchState = {
          ...prev,
          hitsPerPage,
        };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const setPriceRange = useCallback(
    (min: number, max: number) => {
      setState(prev => {
        const next: GoogleSearchState = {
          ...prev,
          facets: [...prev.facets.filter(f => f.name !== 'Price'), { name: 'Price', value: [min, max] }],
        };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const clearPriceRange = useCallback(() => {
    setState(prev => {
      const next: GoogleSearchState = {
        ...prev,
        facets: prev.facets.filter(f => f.name !== 'Price'),
      };
      pushUrl(next);
      return next;
    });
  }, [pushUrl]);

  const setSort = useCallback(
    (sortBy: string | undefined, order: 'asc' | 'desc' | undefined) => {
      setState(prev => {
        const next: GoogleSearchState = { ...prev, sortBy, order };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const setSaleItems = useCallback(
    (isSaleItems: boolean) => {
      setState(prev => {
        const next: GoogleSearchState = { ...prev, isSaleItems };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const setClearanceItems = useCallback(
    (isClearanceItems: boolean) => {
      setState(prev => {
        const next: GoogleSearchState = { ...prev, isClearanceItems };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const setExcludeMarketplace = useCallback(
    (isExcludeMarketplace: boolean) => {
      setState(prev => {
        const next: GoogleSearchState = { ...prev, isExcludeMarketplace };
        pushUrl(next);
        return next;
      });
    },
    [pushUrl]
  );

  const clearAllFacets = useCallback(() => {
    setState(prev => {
      const next: GoogleSearchState = {
        ...prev,
        facets: [],
        isSaleItems: false,
        isClearanceItems: false,
        isExcludeMarketplace: false,
      };
      pushUrl(next);
      return next;
    });
  }, [pushUrl]);

  return {
    state,
    setHitsPerPage,
    setFacet,
    setPriceRange,
    clearPriceRange,
    setSort,
    setSaleItems,
    setClearanceItems,
    setExcludeMarketplace,
    clearAllFacets,
  };
};
